import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Calculator, TrendingUp, DollarSign, ArrowRight, Info, CheckCircle2, XCircle } from 'lucide-react';

// OTA Commission rates
const OTA_COMMISSIONS = {
  booking: { name: 'Booking.com', commission: 0.18 },
  airbnb: { name: 'Airbnb', commission: 0.15 },
  expedia: { name: 'Expedia', commission: 0.20 }
};

// Booking.com discount programs
const BOOKING_DISCOUNTS = {
  genius1: { name: 'Genius Nivel 1', pct: 0.10, type: 'genius', combinable: true },
  genius2: { name: 'Genius Nivel 2', pct: 0.15, type: 'genius', combinable: true },
  genius3: { name: 'Genius Nivel 3', pct: 0.20, type: 'genius', combinable: true },
  mobile: { name: 'Descuento Móvil', pct: 0.10, type: 'principal', combinable: false },
  early_booking_7: { name: 'Early Booking (7 días)', pct: 0.07, type: 'principal', combinable: false },
  early_booking_28: { name: 'Early Booking (28 días)', pct: 0.15, type: 'principal', combinable: false },
  min_stay_2: { name: 'Estancia Mín. 2 noches', pct: 0.05, type: 'principal', combinable: false },
  min_stay_3: { name: 'Estancia Mín. 3 noches', pct: 0.10, type: 'principal', combinable: false },
  nacional: { name: 'Dto. Nacional', pct: 0.10, type: 'segmento', combinable: true },
  internacional: { name: 'Dto. Internacional', pct: 0.10, type: 'segmento', combinable: true }
};

// Expedia discount programs
const EXPEDIA_DISCOUNTS = {
  member_only: { name: 'Member Only Deals', pct: 0.10 },
  mobile_exp: { name: 'Descuento Móvil', pct: 0.10 },
  package: { name: 'Paquete Opaco', pct: 0.15 }
};

// Airbnb discount programs
const AIRBNB_DISCOUNTS = {
  weekly: { name: 'Descuento Semanal', pct: 0.10 },
  monthly: { name: 'Descuento Mensual', pct: 0.20 },
  early_bird: { name: 'Reserva Anticipada', pct: 0.10 },
  last_minute: { name: 'Última Hora', pct: 0.10 }
};

function calculateBaseRate(desiredNet, commissionRate, activeDiscounts) {
  let totalDiscountFactor = 1;
  activeDiscounts.forEach(d => {
    totalDiscountFactor *= (1 - d.pct);
  });
  // BaseRate = DesiredNet / (1 - commission) / discountFactor
  return desiredNet / (1 - commissionRate) / totalDiscountFactor;
}

function formatCurrency(n) {
  return '$' + Math.round(n).toLocaleString('es-CO');
}

export default function SimuladorPage() {
  const [desiredCouple, setDesiredCouple] = useState('');
  const [desiredSingle, setDesiredSingle] = useState('');
  const [activeBookingDiscounts, setActiveBookingDiscounts] = useState({});
  const [activeExpediaDiscounts, setActiveExpediaDiscounts] = useState({});
  const [activeAirbnbDiscounts, setActiveAirbnbDiscounts] = useState({});
  const [results, setResults] = useState(null);

  const toggleBookingDiscount = (key) => {
    const disc = BOOKING_DISCOUNTS[key];
    const newActive = { ...activeBookingDiscounts };

    if (newActive[key]) {
      delete newActive[key];
    } else {
      // If it's a principal type, remove other principal types (they don't combine)
      if (disc.type === 'principal') {
        Object.keys(newActive).forEach(k => {
          if (BOOKING_DISCOUNTS[k]?.type === 'principal') delete newActive[k];
        });
      }
      // If it's genius, remove other genius levels (only one at a time)
      if (disc.type === 'genius') {
        Object.keys(newActive).forEach(k => {
          if (BOOKING_DISCOUNTS[k]?.type === 'genius') delete newActive[k];
        });
      }
      newActive[key] = disc;
    }
    setActiveBookingDiscounts(newActive);
  };

  const toggleExpediaDiscount = (key) => {
    setActiveExpediaDiscounts(prev => {
      const n = { ...prev };
      if (n[key]) delete n[key]; else n[key] = EXPEDIA_DISCOUNTS[key];
      return n;
    });
  };

  const toggleAirbnbDiscount = (key) => {
    setActiveAirbnbDiscounts(prev => {
      const n = { ...prev };
      if (n[key]) delete n[key]; else n[key] = AIRBNB_DISCOUNTS[key];
      return n;
    });
  };

  const simulate = () => {
    const couple = parseFloat(desiredCouple) || 0;
    const single = parseFloat(desiredSingle) || 0;
    if (!couple && !single) return;

    const res = {};
    ['booking', 'airbnb', 'expedia'].forEach(ota => {
      const comm = OTA_COMMISSIONS[ota].commission;
      let discounts = [];
      if (ota === 'booking') discounts = Object.values(activeBookingDiscounts);
      else if (ota === 'expedia') discounts = Object.values(activeExpediaDiscounts);
      else discounts = Object.values(activeAirbnbDiscounts);

      const coupleBase = couple ? calculateBaseRate(couple, comm, discounts) : 0;
      const singleBase = single ? calculateBaseRate(single, comm, discounts) : 0;

      const totalDiscountPct = discounts.reduce((acc, d) => acc + d.pct, 0);
      const coupleAfterDiscount = coupleBase * (1 - totalDiscountPct);
      const singleAfterDiscount = singleBase * (1 - totalDiscountPct);

      const coupleCommission = coupleBase * comm;
      const singleCommission = singleBase * comm;

      res[ota] = {
        name: OTA_COMMISSIONS[ota].name,
        commission: comm,
        discounts,
        couple: { desired: couple, baseRate: coupleBase, afterDiscount: coupleAfterDiscount, commissionAmount: coupleCommission, net: couple },
        single: { desired: single, baseRate: singleBase, afterDiscount: singleAfterDiscount, commissionAmount: singleCommission, net: single }
      };
    });

    // Find a unified base rate (the highest one needed to cover all OTAs)
    const maxCoupleBase = Math.max(...Object.values(res).map(r => r.couple.baseRate));
    const maxSingleBase = Math.max(...Object.values(res).map(r => r.single.baseRate));

    setResults({
      baseRateCouple: maxCoupleBase,
      baseRateSingle: maxSingleBase,
      desiredCouple: couple,
      desiredSingle: single,
      byOTA: res
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="simulador-page">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Simulador de Tarifas</h1>
          <p className="text-muted-foreground mt-1">Calcula tu tarifa base recomendada según el ingreso deseado y los descuentos activos</p>
        </div>

        {/* Input Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
              <Calculator className="h-5 w-5" />
              Ingreso Deseado por Noche
            </CardTitle>
            <CardDescription>Indica cuánto deseas recibir como ingreso neto después de comisiones y descuentos</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Ingreso deseado por Pareja (USD)</Label>
                <Input
                  type="number"
                  placeholder="Ej: 100"
                  value={desiredCouple}
                  onChange={e => setDesiredCouple(e.target.value)}
                  data-testid="input-desired-couple"
                />
              </div>
              <div>
                <Label>Ingreso deseado por Persona Sola (USD)</Label>
                <Input
                  type="number"
                  placeholder="Ej: 90"
                  value={desiredSingle}
                  onChange={e => setDesiredSingle(e.target.value)}
                  data-testid="input-desired-single"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Booking.com Discounts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              <span className="text-blue-700">Booking.com</span> - Promociones
            </CardTitle>
            <CardDescription>
              <Info className="h-3 w-3 inline mr-1" />
              Los descuentos principales NO se combinan entre sí. Solo se combinan con Genius y segmentación (Nacional/Internacional).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Programa Genius (selecciona uno)</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {Object.entries(BOOKING_DISCOUNTS).filter(([, v]) => v.type === 'genius').map(([key, disc]) => (
                  <button
                    key={key}
                    onClick={() => toggleBookingDiscount(key)}
                    className={`p-3 rounded-lg border text-left transition-all ${activeBookingDiscounts[key] ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`booking-discount-${key}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{disc.name}</span>
                      <Badge variant={activeBookingDiscounts[key] ? 'default' : 'outline'}>{(disc.pct * 100)}%</Badge>
                    </div>
                  </button>
                ))}
              </div>

              <Separator />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Descuento Principal (selecciona uno, no se combinan)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(BOOKING_DISCOUNTS).filter(([, v]) => v.type === 'principal').map(([key, disc]) => (
                  <button
                    key={key}
                    onClick={() => toggleBookingDiscount(key)}
                    className={`p-3 rounded-lg border text-left transition-all ${activeBookingDiscounts[key] ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`booking-discount-${key}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{disc.name}</span>
                      <Badge variant={activeBookingDiscounts[key] ? 'default' : 'outline'}>{(disc.pct * 100)}%</Badge>
                    </div>
                  </button>
                ))}
              </div>

              <Separator />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Segmentación (se combinan con los anteriores)</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(BOOKING_DISCOUNTS).filter(([, v]) => v.type === 'segmento').map(([key, disc]) => (
                  <button
                    key={key}
                    onClick={() => toggleBookingDiscount(key)}
                    className={`p-3 rounded-lg border text-left transition-all ${activeBookingDiscounts[key] ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`booking-discount-${key}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{disc.name}</span>
                      <Badge variant={activeBookingDiscounts[key] ? 'default' : 'outline'}>{(disc.pct * 100)}%</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Airbnb & Expedia in 2 columns */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                <span className="text-red-600">Airbnb</span> - Promociones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(AIRBNB_DISCOUNTS).map(([key, disc]) => (
                  <button
                    key={key}
                    onClick={() => toggleAirbnbDiscount(key)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${activeAirbnbDiscounts[key] ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`airbnb-discount-${key}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{disc.name}</span>
                      <Badge variant={activeAirbnbDiscounts[key] ? 'destructive' : 'outline'}>{(disc.pct * 100)}%</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                <span className="text-yellow-600">Expedia</span> - Promociones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(EXPEDIA_DISCOUNTS).map(([key, disc]) => (
                  <button
                    key={key}
                    onClick={() => toggleExpediaDiscount(key)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${activeExpediaDiscounts[key] ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`expedia-discount-${key}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{disc.name}</span>
                      <Badge variant={activeExpediaDiscounts[key] ? 'default' : 'outline'} className={activeExpediaDiscounts[key] ? 'bg-yellow-500' : ''}>
                        {(disc.pct * 100)}%
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calculate Button */}
        <Button size="lg" className="w-full py-6 text-base font-bold" onClick={simulate} data-testid="simulate-btn">
          <Calculator className="h-5 w-5 mr-2" />
          Calcular Tarifa Base Recomendada
        </Button>

        {/* Results */}
        {results && (
          <div className="space-y-6" data-testid="simulation-results">
            {/* Main Recommendation */}
            <Card className="border-2 border-primary bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Manrope' }}>
                  <TrendingUp className="h-6 w-6" />
                  Tarifa Base Recomendada
                </CardTitle>
                <CardDescription>Esta es la tarifa que debes configurar en tu sistema para distribuir a los canales de venta</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-6">
                  {results.desiredCouple > 0 && (
                    <div className="text-center p-6 bg-white rounded-xl border-2 border-primary/20">
                      <p className="text-sm text-muted-foreground mb-2">Por Pareja</p>
                      <p className="text-4xl font-extrabold text-primary" data-testid="result-base-couple">
                        {formatCurrency(results.baseRateCouple)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Para recibir {formatCurrency(results.desiredCouple)} neto
                      </p>
                    </div>
                  )}
                  {results.desiredSingle > 0 && (
                    <div className="text-center p-6 bg-white rounded-xl border-2 border-primary/20">
                      <p className="text-sm text-muted-foreground mb-2">Por Persona Sola</p>
                      <p className="text-4xl font-extrabold text-primary" data-testid="result-base-single">
                        {formatCurrency(results.baseRateSingle)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Para recibir {formatCurrency(results.desiredSingle)} neto
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Per OTA Breakdown */}
            <div className="grid lg:grid-cols-3 gap-4">
              {Object.entries(results.byOTA).map(([ota, data]) => {
                const otaColors = { booking: 'blue', airbnb: 'red', expedia: 'yellow' };
                const color = otaColors[ota];
                return (
                  <Card key={ota} data-testid={`result-ota-${ota}`}>
                    <CardHeader className="pb-3">
                      <CardTitle className={`text-base text-${color}-700`} style={{ fontFamily: 'Manrope' }}>{data.name}</CardTitle>
                      <Badge variant="outline">Comisión: {(data.commission * 100)}%</Badge>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {data.couple.desired > 0 && (
                        <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                          <p className="text-xs font-semibold">Pareja</p>
                          <div className="flex justify-between text-sm">
                            <span>Tarifa publicada:</span>
                            <span className="font-bold">{formatCurrency(data.couple.baseRate)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Después de descuentos:</span>
                            <span>{formatCurrency(data.couple.afterDiscount)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-red-500">
                            <span>Comisión OTA:</span>
                            <span>-{formatCurrency(data.couple.commissionAmount)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between text-sm font-bold text-green-600">
                            <span>Ingreso neto:</span>
                            <span>{formatCurrency(data.couple.net)}</span>
                          </div>
                        </div>
                      )}
                      {data.single.desired > 0 && (
                        <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                          <p className="text-xs font-semibold">Individual</p>
                          <div className="flex justify-between text-sm">
                            <span>Tarifa publicada:</span>
                            <span className="font-bold">{formatCurrency(data.single.baseRate)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Después de descuentos:</span>
                            <span>{formatCurrency(data.single.afterDiscount)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-red-500">
                            <span>Comisión OTA:</span>
                            <span>-{formatCurrency(data.single.commissionAmount)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between text-sm font-bold text-green-600">
                            <span>Ingreso neto:</span>
                            <span>{formatCurrency(data.single.net)}</span>
                          </div>
                        </div>
                      )}

                      {/* Active discounts */}
                      {data.discounts.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground mb-1">Descuentos activos:</p>
                          <div className="flex flex-wrap gap-1">
                            {data.discounts.map((d, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
                                {d.name} ({Math.round(d.pct * 100)}%)
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {data.discounts.length === 0 && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Sin descuentos activos
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Recommendations */}
            <Card className="bg-muted/30">
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Recomendaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Configura la <strong>tarifa base recomendada</strong> en tu sistema de tarifas. Esta será distribuida automáticamente a cada canal de venta por el Channel Manager.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Activa las <strong>promociones seleccionadas</strong> directamente en cada plataforma (Booking, Airbnb, Expedia) para maximizar visibilidad y conversión.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Recuerda que en <strong>Booking.com</strong>, los descuentos principales (Early Booking, Estancia Mínima, Móvil) NO se combinan entre sí, pero SÍ se combinan con Genius y segmentación Nacional/Internacional.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Revisa periódicamente estos cálculos cuando cambien las comisiones de las OTAs o cuando ajustes tu estrategia de descuentos.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
